/**
 * MissionControlScreen — visão transversal de MONITORAMENTO (Mission Control).
 *
 * Projeção read-only sobre o core (Temporal continua dono da orquestração). Abas, todas
 * alimentadas por telemetria que JÁ existe:
 *   - Trace/Execução  → waterfall por fase, com os agentes que atuaram em cada uma
 *                       (GET /executions/{id}/trace)
 *   - AgentRoom       → sala dos agentes da execução
 *   - Topologia       → grafo do squad (GET /executions/{id}/squad) via mermaid
 *   - Skills          → índice skill → agentes do catálogo
 * A aba Observabilidade saiu em 2026-10-06 (decisão do dono): era redundante com a tela
 * Observabilidade de Agentes (`/agents/observabilidade`), que tem o dado por projeto.
 *
 * Complementa o Live Board (log ao vivo de UMA execução) com a visão temporal e
 * estrutural. Paleta C, estilos inline, pt-BR, a11y.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Radio, Activity, GitBranch, BarChart3, Clock, AlertTriangle, CheckCircle2,
  Loader2, ChevronRight, Users, Wrench, X,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge, StatusDot } from '@/components/ui';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { MermaidBlock } from '@/components/MermaidView';
import { useNavigationContext } from '@/navigation';
import {
  useExecution, useExecutionTrace, useExecutionSquad,
} from '@/api/executions';
import { useRecentProjects, useDashboardKpis } from '@/api/dashboard';
import { MissionPanel } from '@/ui/sphere/MissionPanel';
import { useMissao } from '@/ui/sphere/useMissao';
import { numeroOuNulo } from '@/lib/format';
import { AgentRoom } from './missionControl/AgentRoom';
import { SkillView } from './missionControl/SkillView';
import { DetalheDosNos, ElencoDaExecucao } from './missionControl/ElencoDaExecucao';
import { CycleStrip } from './liveboard/UnifiedGatesPanel';
import { deriveAgentRuntime } from '@/lib/agentRuntime';

const EXEC_TERMINAL = new Set(['done', 'completed', 'failed', 'cancelled', 'sla_expired']);

// ─── Helpers de formatação ────────────────────────────────────────────────────

function fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
}

const STATUS_COLOR = {
  completed: C.cyber,
  running:   C.warn,
  failed:    C.hot,
  rejected:  C.hot,
};

function spanColor(span) {
  if (span.status && STATUS_COLOR[span.status]) return STATUS_COLOR[span.status];
  if (span.kind === 'gate') return C.quantum ?? C.cyber;
  return C.neutralDim;
}

// ─── Seletor de execução (quando não há ?execution=) ──────────────────────────

function ExecutionPicker({ onPick }) {
  const { data: projects, isLoading } = useRecentProjects();
  const runnable = (projects ?? []).filter((p) => p.temporal_workflow_id);

  if (isLoading) return <LoadingSpinner label="Carregando execuções..." />;
  if (!runnable.length) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: C.neutralMuted }}>
        Nenhuma execução encontrada. Inicie um projeto no Dashboard.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 560 }}>
      <div style={{ fontSize: 13, color: C.neutralDim, marginBottom: 4 }}>
        Escolha uma execução para monitorar:
      </div>
      {runnable.map((p) => (
        <button
          key={p.id}
          onClick={() => onPick(p.temporal_workflow_id)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
            background: C.blackElev, border: `1px solid ${C.blackBorder}`,
            borderRadius: 8, cursor: 'pointer', textAlign: 'left', color: C.neutral,
          }}
        >
          <StatusDot status={p.status} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
            <div style={{ fontSize: 11, color: C.neutralMuted }}>
              #{String(p.temporal_workflow_id).slice(0, 8)}
            </div>
          </div>
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

/**
 * Agentes que atuaram numa fase: a lista que a API devolve (`agents`, do que mais atuou ao
 * que menos); resposta antiga, sem a lista, cai no agente único da fase.
 * Execução eb378ddc (2026-10-06): a "Geração de Código" mostrava só o Tech Lead enquanto o
 * Live Board trazia seis especialistas na mesma fase.
 */
export function agentesDaFase(span) {
  if (Array.isArray(span?.agents) && span.agents.length) return span.agents;
  return span?.agent_id ? [{ agent_id: span.agent_id, events: null }] : [];
}

// ─── Aba: Trace / Execução (waterfall) ────────────────────────────────────────

function TraceTab({ executionId, focusedAgent, onClearFocus }) {
  const poll = useMemo(
    () => (q) => (EXEC_TERMINAL.has(String(q.state.data?.execution?.status ?? '').toLowerCase()) ? false : 5000),
    [],
  );
  const { data, isLoading, error, refetch } = useExecutionTrace(executionId, { refetchInterval: poll });
  const [openSpan, setOpenSpan] = useState(null);
  const focusId = focusedAgent?.id;

  if (isLoading) return <LoadingSpinner label="Montando o trace..." />;
  if (error) return <QueryError error={error} onRetry={() => refetch()} />;

  const spans = data?.spans ?? [];
  const maxDur = Math.max(1, ...spans.map((s) => s.duration_ms ?? 0));
  const events = data?.events ?? [];

  return (
    <div>
      {/* KPIs do topo */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <Kpi icon={Clock} label="Duração total" value={fmtDuration(data?.execution?.duration_ms)} />
        <Kpi icon={Activity} label="Fases" value={spans.length} />
        <Kpi icon={BarChart3} label="Eventos" value={data?.execution?.event_count ?? 0} />
      </div>

      {/* Cross-nav: chip do agente em foco (vindo do AgentRoom/Skills) */}
      {focusedAgent && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12,
          fontSize: 12, color: C.cyber, background: `${C.cyber}12`,
          border: `1px solid ${C.cyber}33`, borderRadius: 14, padding: '3px 6px 3px 10px' }}>
          Foco: <strong>{focusedAgent.name}</strong>
          <button onClick={onClearFocus} aria-label="Limpar foco"
            style={{ background: 'transparent', border: 'none', color: C.cyber,
              cursor: 'pointer', display: 'flex' }}>
            <X size={12} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Waterfall */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {spans.map((s) => {
          const pct = Math.max(3, Math.round(((s.duration_ms ?? 0) / maxDur) * 100));
          const col = spanColor(s);
          const isOpen = openSpan === s.id;
          return (
            <div key={s.id}>
              <button
                onClick={() => setOpenSpan(isOpen ? null : s.id)}
                style={{
                  display: 'grid', gridTemplateColumns: '190px 1fr 84px', alignItems: 'center',
                  gap: 12, width: '100%', background: 'transparent', border: 'none',
                  cursor: 'pointer', padding: '4px 0', textAlign: 'left',
                  opacity: focusId && !agentesDaFase(s).some((a) => a.agent_id === focusId) ? 0.35 : 1,
                  transition: 'opacity 200ms',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: 13, color: C.neutral, fontWeight: 500 }}>
                  {s.status === 'running'
                    ? <Loader2 size={12} style={{ color: col, animation: 'navaSpin 1s linear infinite' }} />
                    : s.status === 'failed' || s.status === 'rejected'
                      ? <AlertTriangle size={12} style={{ color: col }} />
                      : <CheckCircle2 size={12} style={{ color: col }} />}
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.name}
                  </span>
                </span>
                <span style={{ position: 'relative', height: 20, background: C.blackElev,
                  borderRadius: 4, overflow: 'hidden' }}>
                  <span style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`,
                    background: `${col}${s.status === 'running' ? '55' : 'cc'}`,
                    borderRadius: 4, transition: 'width 300ms',
                  }} />
                  {agentesDaFase(s).length > 0 && (
                    <span data-testid={`agentes-da-fase-${s.id}`}
                      title={agentesDaFase(s).map((a) => `${a.agent_id} (${a.events ?? '—'} evento(s))`).join(' · ')}
                      style={{ position: 'absolute', left: 8, right: 8, top: 2, fontSize: 10.5,
                        color: C.neutral, opacity: 0.9, whiteSpace: 'nowrap', overflow: 'hidden',
                        textOverflow: 'ellipsis' }}>
                      {agentesDaFase(s).map((a, i) => (
                        <span key={a.agent_id}>
                          {i > 0 ? ' · ' : ''}{a.agent_id}
                          {agentesDaFase(s).length > 1 && a.events != null ? ` ×${a.events}` : ''}
                        </span>
                      ))}
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 12, color: C.neutralDim, textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums' }}>
                  {fmtDuration(s.duration_ms)}
                </span>
              </button>
              {isOpen && (
                <div style={{ margin: '4px 0 8px 202px', padding: '8px 12px',
                  background: C.blackElev, borderRadius: 6, border: `1px solid ${C.blackBorder}` }}>
                  {events.filter((e) => e.phase === s.phase).slice(-40).map((e, i) => (
                    <div key={i} style={{ fontSize: 11.5, color: C.neutralDim,
                      fontFamily: 'monospace', padding: '1px 0' }}>
                      {e.message || e.event_type}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {!spans.length && (
          <div style={{ padding: 24, color: C.neutralMuted, textAlign: 'center' }}>
            Sem eventos ainda — a execução vai preenchendo o trace ao vivo.
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
      background: C.blackElev, borderRadius: 8, border: `1px solid ${C.blackBorder}` }}>
      <Icon size={16} style={{ color: C.cyber }} aria-hidden="true" />
      <div>
        <div style={{ fontSize: 11, color: C.neutralMuted }}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.neutral,
          fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      </div>
    </div>
  );
}

// ─── Aba: Topologia (grafo do squad via mermaid) ──────────────────────────────

function TopologyTab({ executionId }) {
  const { data, isLoading, error, refetch } = useExecutionSquad(executionId);
  // Overlay runtime: colore os nós pelo estado ao vivo dos agentes (do /trace).
  const trace = useExecutionTrace(executionId);
  const runtime = useMemo(() => {
    const map = {};
    for (const a of deriveAgentRuntime(trace.data?.events ?? [], data?.nodes ?? [], false)) {
      map[a.agentId] = a.active ? 'ativo' : a.status;
    }
    return map;
  }, [trace.data, data]);

  const code = useMemo(() => {
    const nodes = data?.nodes ?? [];
    if (!nodes.length) return '';
    const lines = ['flowchart TD', '  TL(["Tech Lead / Orquestrador"])'];
    // classDefs de status (mermaid) para o overlay runtime.
    lines.push('  classDef ativo fill:#01e6e422,stroke:#01e6e4,stroke-width:2px;');
    lines.push('  classDef concluido fill:#01e6e411,stroke:#01e6e4;');
    lines.push('  classDef falhou fill:#ff330022,stroke:#ff3300,stroke-width:2px;');
    const classes = [];
    nodes.forEach((n, i) => {
      const id = `A${i}`;
      const label = String(n.node_label || n.agent_id || `Agente ${i}`).replace(/["\n]/g, ' ');
      const tier = n.tier ? ` · ${n.tier}` : '';
      lines.push(`  ${id}["${label}${tier}"]`);
      lines.push(`  TL --> ${id}`);
      const st = runtime[n.agent_id] || runtime[n.node_label];
      if (st === 'ativo' || st === 'typing' || st === 'reading' || st === 'thinking') classes.push(`  class ${id} ativo;`);
      else if (st === 'completed') classes.push(`  class ${id} concluido;`);
      else if (st === 'failed') classes.push(`  class ${id} falhou;`);
    });
    // Tech-lead runtime.
    const tl = runtime['tech-lead-agent'];
    if (tl === 'ativo') classes.push('  class TL ativo;');
    return [...lines, ...classes].join('\n');
  }, [data, runtime]);

  if (isLoading) return <LoadingSpinner label="Carregando topologia..." />;
  if (error) return <QueryError error={error} onRetry={() => refetch()} />;
  if (!code) {
    return (
      <div style={{ padding: 24, color: C.neutralMuted, textAlign: 'center' }}>
        O squad ainda não foi montado para esta execução.
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.neutral }}>
          {data?.squad_name || 'Squad'}
        </span>
        {data?.mission && (
          <span style={{ fontSize: 12, color: C.neutralMuted }}>— {data.mission}</span>
        )}
        <Badge color={C.cyberDim} light>{(data?.nodes ?? []).length} agentes</Badge>
      </div>
      <Card style={{ padding: 16, overflow: 'auto' }}>
        <div className="nava-topology">
          <MermaidBlock code={code} />
        </div>
      </Card>
      <DetalheDosNos nodes={data?.nodes} />
      <ElencoDaExecucao executionId={executionId} />
    </div>
  );
}

// ─── Tela principal ───────────────────────────────────────────────────────────

const TABS = [
  { id: 'trace', label: 'Trace / Execução', icon: Activity },
  { id: 'agentroom', label: 'AgentRoom', icon: Users },
  { id: 'topology', label: 'Topologia', icon: GitBranch },
  { id: 'skills', label: 'Skills', icon: Wrench },
];

export function MissionControlScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const executionId = searchParams.get('execution');
  const { navigate } = useNavigationContext();
  const [tab, setTab] = useState('trace');
  const [focusedAgent, setFocusedAgent] = useState(null);

  const { data: execution } = useExecution(executionId);
  const { data: traceMeta } = useExecutionTrace(executionId);
  const { data: kpis } = useDashboardKpis();
  const missao = useMissao(executionId);
  const cyc = traceMeta?.execution;
  const status = execution?.status;
  const live = status && !EXEC_TERMINAL.has(String(status).toLowerCase());

  const pick = (eid) => setSearchParams({ execution: eid });
  // Cross-navigation (§12): focar um agente leva à aba-alvo com o foco aplicado.
  const focusAgent = (agent, target = 'trace') => {
    setFocusedAgent(agent);
    setTab(target);
  };

  return (
    <div style={{ padding: '8px 22px 28px' }}>
      {/* Topo Sphere: núcleo orbital, macroetapas e resumo da execução selecionada.
          Agentes ativos = desta execução (eventos na janela); projetos em andamento e
          gates pendentes = do tenant (KPIs). O conteúdo real (trace, grafo…) segue abaixo. */}
      <div className="sp-mc-top">
        <MissionPanel
          headingLevel={1}
          eyebrow={executionId ? `Monitoramento · #${executionId.slice(0, 8)}` : 'Monitoramento'}
          subtitle={executionId
            ? (missao.projeto ? `Execução de ${missao.projeto}` : 'Execução selecionada')
            : 'Selecione abaixo a execução que deseja acompanhar.'}
          snapshot={missao.snapshot}
          conexao={missao.conexao}
          acompanhando={missao.projeto}
          counts={{
            activeAgents: executionId ? (missao.snapshot?.activeAgents ?? null) : numeroOuNulo(kpis?.agentesEmExecucao),
            activeProjects: numeroOuNulo(kpis?.projetosEmAndamento),
            pendingGates: numeroOuNulo(kpis?.gateAprovacoes),
          }}
          onGates={() => navigate('gates')}
          onRetry={missao.refetch}
        />
      </div>

      {executionId && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <Badge color={C.cyberDim} light>#{executionId.slice(0, 8)}</Badge>
          {cyc?.cycles_consolidated ? (
            <Badge color={C.quantum ?? C.cyber} light>
              {live ? '🎯' : '✅'} {cyc.cycle_total ? `${cyc.cycle_total} ciclos` : 'Ciclos'} · {live ? 'consolidando' : 'consolidados'}
            </Badge>
          ) : cyc?.current_cycle ? (
            <Badge color={C.quantum ?? C.cyber} light>
              🔄 Ciclo {cyc.current_cycle}{cyc.cycle_total ? `/${cyc.cycle_total}` : ''}
            </Badge>
          ) : null}
          {status && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11,
              fontWeight: 700, letterSpacing: '0.06em',
              color: live ? C.cyber : C.neutralMuted }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%',
                backgroundColor: live ? C.cyber : C.neutralMuted,
                boxShadow: live ? `0 0 8px ${C.cyber}` : 'none' }} />
              {live ? 'AO VIVO' : String(status).toUpperCase()}
            </span>
          )}
          <Btn variant="ghost" size="sm" icon={Radio}
            onClick={() => navigate(`/execution/liveboard?execution=${executionId}`)}>
            Abrir Live Board
          </Btn>
          <Btn variant="ghost" size="sm"
            onClick={() => setSearchParams({})}>
            Trocar execução
          </Btn>
        </div>
      )}

      {!executionId ? (
        <Card style={{ padding: 24 }}>
          <ExecutionPicker onPick={pick} />
        </Card>
      ) : (
        <>
          {/* Stepper de ciclos (prominente) quando o run é multi-ciclo.
              Consolidado: a entrega cobre TODOS os ciclos numa passada — o strip
              mostra todos concluídos quando a execução termina (não "Ciclo 1/N"). */}
          {cyc?.cycle_total > 1 && (
            <div style={{ background: C.blackElev, border: `1px solid ${C.blackBorder}`,
              borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <CycleStrip
                cycles={Array.from({ length: cyc.cycle_total }, (_, i) => ({ number: i + 1 }))}
                current={cyc.current_cycle}
                consolidated={!!cyc.cycles_consolidated}
                allDone={!!cyc.cycles_consolidated && !live}
              />
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${C.blackBorder}`,
            marginBottom: 20 }}>
            {TABS.map((t) => {
              const active = t.id === tab;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    fontSize: 13.5, fontWeight: 600,
                    color: active ? C.cyber : C.neutralDim,
                    borderBottom: active ? `2px solid ${C.cyber}` : '2px solid transparent',
                    marginBottom: -1,
                  }}
                >
                  <t.icon size={15} aria-hidden="true" />
                  {t.label}
                </button>
              );
            })}
          </div>

          <Card style={{ padding: 24 }}>
            {tab === 'trace' && (
              <TraceTab executionId={executionId} focusedAgent={focusedAgent}
                onClearFocus={() => setFocusedAgent(null)} />
            )}
            {tab === 'agentroom' && (
              <AgentRoom executionId={executionId} focusedAgent={focusedAgent}
                onFocusAgent={(a) => focusAgent(a, 'trace')} />
            )}
            {tab === 'topology' && <TopologyTab executionId={executionId} />}
            {tab === 'skills' && (
              <SkillView onFocusAgent={(a) => focusAgent(a, 'agentroom')} />
            )}
          </Card>
        </>
      )}
    </div>
  );
}
